import * as ga from "../../../lib/ga";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { FieldValues, useForm, UseFormReturn } from "react-hook-form";
import RailwayStaffUserDetailsRoute from "../../../pages/mutual-transfer/railway-staff/[userId]";
import { useAuth } from "../../auth/AuthProvider";
import Button from "../../components/atoms/Button/Button";
import SearchHeadText from "../../components/atoms/SearchHeadText/SearchHeadText";
import LoginNotice from "../../components/molecules/LoginNotice/LoginNotice";
import { HSelect } from "../../components/molecules/Select/Select";
import {
  getRailwayStaffDivision,
  RAILWAY_STAFF_DESIGNATION,
  RAILWAY_STAFF_RAILWAYS,
  RAILWAY_STAFF_ZONAL_HEAD,
} from "../../components/molecules/Select/select-options";
import NoResultFound from "../../components/organisms/NoResultFound/NoResultFound";
import VirtualLoadMore from "../../components/organisms/VirtualLoadMore/VirtualLoadMore";
import AccountBriefInfo from "../../components/templates/AccountBriefInfo/AccountBriefInfo";
import SearchPageTemplate from "../../components/templates/SearchPageTemplate/SearchPageTemplate";
import { MUTUAL_TRANSFER_CATEGORIES } from "../../db/types/common";
import {
  RailwayStaffDefaultFields,
  RailWayStaffUser,
  ServiceInfoWithOutPaidInfo,
} from "../../db/types/RailwayStaff";
import { convertRailwayStaffDataToKeys } from "../../hooks/matchers/useMatchers";
import { getNoResultMsg, removeMyProfile } from "../../util";
import {
  LOADING_LIMIT_IN_SEARCH_PAGE,
  shouldLoadMoreDataInSearchPage,
  Urls,
  WithLastDoc,
} from "../../util/constant";
import { firestore } from "../../util/firebase";
import styles from "./../HighSchoolSearchPage/HighSchoolSearchPage.module.css";

interface SearchQuery {
  railway: string;
  designation: string;
  zonal_head: string;
  division: string;
}

interface NonTeachingStaffSearchProps {
  formReturn: UseFormReturn<FieldValues>;
  onSearch: (data: SearchQuery) => void;
}

const PrimarySchoolSearch: React.FC<NonTeachingStaffSearchProps> = ({
  formReturn,
  onSearch,
}) => {
  const {
    watch,
    formState: { errors },
    setValue,
    handleSubmit,
    register,
  } = formReturn;

  const zonalHead = watch("zonal_head");
  const division = watch("division");

  const SELECTED_RAILWAY_STAFF_DIVISION = getRailwayStaffDivision(zonalHead);

  //#region
  useEffect(() => {
    if (
      division !== "" &&
      !SELECTED_RAILWAY_STAFF_DIVISION.map((e) => e.value).includes(division)
    ) {
      setValue("division", "");
    }
  }, [zonalHead, division]);

  //#endregion

  return (
    <div className={styles.head}>
      <SearchHeadText label="Railway Staff" />

      <form
        method="get"
        className={`${styles.search} st-50-50`}
        onSubmit={handleSubmit(onSearch)}
      >
        <HSelect
          hideLabel
          label="Railway"
          options={[
            { label: "Select Railway", value: "" },
            ...RAILWAY_STAFF_RAILWAYS,
          ]}
          registerProps={register("railway", { required: true })}
          error={errors.railway}
        />
        <HSelect
          hideLabel
          label="Zonal Head"
          options={[
            { label: "Select Zonal Head", value: "" },
            ...RAILWAY_STAFF_ZONAL_HEAD,
          ]}
          registerProps={register("zonal_head", { required: true })}
          error={errors.zonal_head}
        />
        <HSelect
          hideLabel
          label="Division"
          options={[
            { label: "Select Division", value: "" },
            ...SELECTED_RAILWAY_STAFF_DIVISION,
          ]}
          registerProps={register("division", { required: false })}
          error={errors.division}
        />
        <HSelect
          hideLabel
          label="Designation"
          options={[
            { label: "Select Designation", value: "" },
            ...RAILWAY_STAFF_DESIGNATION,
          ]}
          registerProps={register("designation", { required: false })}
          error={errors.designation}
        />
        <div>
          <Button label="Search" type="submit" onClick={undefined} />
        </div>
      </form>
    </div>
  );
};

type WithId<T> = T & {
  id: string;
};

export interface RailwayStaffSearchPagePops {}

export default function RailwayStaffSearchPage({}: RailwayStaffSearchPagePops) {
  const formReturn = useForm();
  const router = useRouter();
  const [searchResult, setSearchResult] = useState<
    WithId<ServiceInfoWithOutPaidInfo>[]
  >([]);
  //#region
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const { user, initializing } = useAuth();
  const lastItem = useRef(null);
  const cacheQuery = useRef<Partial<SearchQuery>>({});
  const isLoggedIn = !!(initializing === false && user);
  //#endregion

  const loadData = async ({
    designation,
    zonal_head,
    division,
    railway,
    doc,
  }: WithLastDoc<SearchQuery>) => {
    let query = firestore
      .collection(RailWayStaffUser.SERVICE_INFO_COLLECTION_NAME)
      .where("mt_category", "==", MUTUAL_TRANSFER_CATEGORIES.RAILWAY_STAFF);

    let totalReset = {};

    if (designation) {
      totalReset = { ...totalReset, designation };

      query = query.where(
        RailwayStaffDefaultFields.DESIGNATION,
        "==",
        designation
      );
    }
    if (division) {
      totalReset = { ...totalReset, division };

      query = query.where(RailwayStaffDefaultFields.DIVISION, "==", division);
    }
    if (railway) {
      totalReset = { ...totalReset, railway };

      query = query.where(
        RailwayStaffDefaultFields.PRESENT_RAILWAY,
        "==",
        railway
      );
    }

    if (zonal_head) {
      totalReset = { ...totalReset, zonal_head };

      query = query.where(
        RailwayStaffDefaultFields.ZONAL_HEAD,
        "==",
        zonal_head
      );
    }
    formReturn.reset(totalReset);

    query = query.orderBy("postedAt", "desc");

    if (doc) {
      console.log(`query+=startAfter`, doc);
      query = query.startAfter(doc);
    }

    const { docs } = await query.limit(LOADING_LIMIT_IN_SEARCH_PAGE).get();

    return docs;
  };

  const onSearch = (data: SearchQuery) => {
    const { designation, railway, division, zonal_head } = data;

    router.push(
      `?designation=${designation}&railway=${railway}&division=${division}&zonal_head=${zonal_head}`,
      undefined,
      { shallow: true }
    );
    document.querySelector("#search_result").scrollIntoView();
    ga.event({
      category: "Search",
      action: "Search Click",
      label: MUTUAL_TRANSFER_CATEGORIES.RAILWAY_STAFF,
    });
  };

  const _loadNextPage = async (...args) => {
    console.log("loadNextPage");

    let lastDoc = lastItem.current;
    if (typeof args[0] === "boolean" && args[0] === true) lastDoc = undefined;
    setIsNextPageLoading(true);
    const {
      designation,
      railway,
      division,
      zonal_head,
    } = (router?.query as unknown) as SearchQuery;

    console.log(router.query);

    const docs = await loadData({
      designation,
      railway,
      division,
      zonal_head,
      doc: lastDoc,
    });

    const _searchResult = docs.map((e) => {
      const data = e.data() as ServiceInfoWithOutPaidInfo;
      return { ...data, id: e.id };
    });

    lastItem.current = docs[docs.length - 1];

    setIsNextPageLoading(false);
    const hasNext = shouldLoadMoreDataInSearchPage(isLoggedIn, _searchResult);
    setHasNextPage(hasNext);
    setSearchResult((prevItems) =>
      [...(lastDoc === undefined ? [] : prevItems)].concat(_searchResult)
    );
    ga.event({
      category: "Search",
      action: "Load More",
      label: MUTUAL_TRANSFER_CATEGORIES.RAILWAY_STAFF,
    });
  };

  const realHasNextPage =
    searchResult.length === 0 ? hasNextPage : hasNextPage && isLoggedIn;

  const searchResultWithOutLoggedInProfile = removeMyProfile(
    searchResult,
    user
  );
  const noResult =
    hasNextPage === false && searchResultWithOutLoggedInProfile.length === 0;

  const noResultMsg = getNoResultMsg(
    searchResultWithOutLoggedInProfile,
    router.asPath
  );
  const showLoginNotice =
    !isLoggedIn &&
    noResult === false &&
    searchResultWithOutLoggedInProfile.length > 9;

  const userId = router.query.userId;
  const isFirstTime = useRef(true);
  useEffect(() => {
    if (userId) document.body.classList.add("no-scroll");
    else document.body.classList.remove("no-scroll");
  }, [userId]);

  useEffect(() => {
    (async () => {
      const cachedQuery = cacheQuery.current;
      const routerQuery = router?.query as Partial<SearchQuery>;
      if (
        isFirstTime.current === false &&
        cachedQuery.designation === routerQuery.designation &&
        cachedQuery.division === routerQuery.division &&
        cachedQuery.zonal_head === routerQuery.zonal_head &&
        cachedQuery.railway === routerQuery.railway
      ) {
        console.log("skipping thanks to cache", cachedQuery, routerQuery);
      } else {
        console.log("loadInit", cacheQuery.current);
        cacheQuery.current = router?.query;
        setSearchResult([]);
        await _loadNextPage(true);
        isFirstTime.current = false;
      }
    })();
  }, [router?.query]);

  return (
    <>
      <SearchPageTemplate
        exclude="RAILWAY_STAFF"
        searchElement={
          <PrimarySchoolSearch formReturn={formReturn} onSearch={onSearch} />
        }
        searchResult={
          <>
            {noResult ? (
              <NoResultFound label={noResultMsg} />
            ) : (
              <VirtualLoadMore
                hasMoreData={realHasNextPage}
                isLoading={isNextPageLoading}
                items={searchResultWithOutLoggedInProfile}
                loadNextPage={_loadNextPage}
                showEndNotice={!showLoginNotice}
                renderItem={(e) => (
                  <AccountBriefInfo
                    href={`${Urls.RAILWAY_STAFF_URL}/${e.id}`}
                    key={e.id}
                    id={e.id}
                    keys={convertRailwayStaffDataToKeys({
                      designation: e.designation,
                      present_railway: e.present_railway,
                      present_zonal_head: e.zonal_head,
                      division: e.division,
                      // ,
                      department: e.department,
                      choice_zonal_head: e.choice_zonal_head,
                      choice_railway: e.choice_railway,
                      choice_division: e.choice_division,
                      //
                      href: `${Urls.RAILWAY_STAFF_URL}/${e.id}`,
                      id: e.id,
                    })}
                  />
                )}
              />
            )}
            {showLoginNotice && <LoginNotice />}
          </>
        }
      />
      {userId && (
        <div className="pop-up">
          <RailwayStaffUserDetailsRoute />
        </div>
      )}
    </>
  );
}
